/**
 * What happens when the BFF says the session is gone.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY A BRIDGE, RATHER THAN THE REDIRECT THIS REPLACES
 * ─────────────────────────────────────────────────────────────────────────
 * `src/crm/lib/api.ts` is a bare axios instance. It has no router, no auth
 * context and no toast, so the only sign-out it could perform on its own was
 * `window.location.assign('/login')` — a FULL page load, which cannot carry
 * router state. The path the reseller was on went with it: someone whose
 * session expired half way through `/invoices/generate?invoiceId=31` was
 * dropped on /login and, after signing in, landed on `/` rather than back on
 * the invoice they were editing.
 *
 * `RequireAuth` had the answer all along — it navigates with
 * `state={{ from }}`, which `LoginPage` reads — but a hard assign bypasses it
 * entirely. So the axios layer no longer decides anything. It REPORTS, and
 * `SessionExpiryBridge` (mounted inside the router, with the auth context and
 * the toast store in reach) decides what a lost session means.
 *
 * The fallback below exists for the one case the bridge cannot cover: a 401
 * that lands before React has mounted it. That is rare — CRM calls come from
 * mounted components — but "rare" is not "never", and the route has to
 * survive it too, so the fallback carries the path in `?next=` instead of
 * discarding it. `LoginPage` honours both channels; see `safeReturnPath` for
 * why the query-string one is the guarded one.
 */

/** The parts of a location a return path is built from. */
export interface ReturnLocation {
  pathname: string;
  search: string;
  hash: string;
}

/**
 * Called when a 401 says the session is gone. Takes no argument on purpose:
 * the bridge reads the CURRENT router location, which is more accurate than
 * anything the axios layer could pass — an in-flight request may well have
 * been started on a page the reseller has since navigated away from.
 */
export type SessionExpiryHandler = () => void;

/**
 * Pages that render without a session.
 *
 * A 401 on one of these must NOT redirect: signing out someone who is
 * already signed out is how you build a loop. Matched exactly rather than by
 * prefix — `/registered-customers` is not `/register`.
 *
 * `/onboarding` is deliberately absent. An applicant HAS a session (spec E
 * §6.2), so a 401 there is a real expiry and belongs on the sign-in screen.
 */
const PUBLIC_PATHS = new Set(["/login", "/register", "/accept-invitation", "/reseller-program"]);

let handler: SessionExpiryHandler | null = null;

/**
 * Latches once the fallback fires. Only the FALLBACK needs this: a full page
 * load is being replaced already, and starting a second one mid-navigation is
 * at best wasted and at worst a race. The handler path needs no such latch —
 * it dedupes on the auth context, which is real state rather than a flag this
 * module would have no way to clear when the session came back.
 */
let hardRedirectStarted = false;

/**
 * Register the router-aware sign-out. Returns its own unregister, which is
 * a no-op once a NEWER handler has taken over — StrictMode mounts, unmounts
 * and remounts, and a stale cleanup running last must not tear down the live
 * bridge and quietly drop the app back onto the fallback.
 */
export function setSessionExpiryHandler(next: SessionExpiryHandler): () => void {
  handler = next;
  return () => {
    if (handler === next) handler = null;
  };
}

/** Whether `pathname` is a page someone reaches without signing in. */
export function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.has(pathname);
}

/**
 * The path to come back to after signing in.
 *
 * The query is NOT decoration: `/invoices/generate?invoiceId=31` is how the
 * ported code says "edit THIS invoice", so dropping it would turn a return
 * into "start a blank one" — the same trap `stripCrmPrefix` documents in
 * App.tsx, about the same path.
 */
export function returnPathOf(loc: ReturnLocation): string {
  return `${loc.pathname}${loc.search}${loc.hash}`;
}

/**
 * A return path, or `/` if it is anything but an in-app one.
 *
 * `?next=` is attacker-supplied by construction — it sits in a URL anyone can
 * put in front of a reseller — so this is what stops the sign-in screen being
 * an open redirector. Refused: a protocol-relative `//evil.test` (which the
 * browser reads as another ORIGIN despite the leading slash), any absolute or
 * scheme URL, a bare relative path (it would resolve against /login), and
 * `/\evil.test`, which some browsers normalise to `//`.
 */
export function safeReturnPath(raw: string | null | undefined): string {
  if (!raw) return "/";
  if (!raw.startsWith("/")) return "/";
  if (raw.startsWith("//") || raw.startsWith("/\\")) return "/";
  return raw;
}

/**
 * Report that the session is gone. Hands off to the bridge when one is
 * mounted; otherwise falls back to a full load that carries the route.
 */
export function reportSessionExpired(): void {
  if (handler) {
    handler();
    return;
  }

  if (typeof window === "undefined") return;
  if (isPublicPath(window.location.pathname)) return;
  if (hardRedirectStarted) return;

  hardRedirectStarted = true;
  const next = returnPathOf(window.location);
  window.location.assign(`/login?next=${encodeURIComponent(next)}`);
}
